/**
 * HVP Cascade Controller - WooCommerce/plugin coordination
 *
 * Neutralizes the native wc_variation_form handlers, owns the global
 * coordination flags (__hvp_dom_updating / __hvp_event_handled) and
 * registers with the HUV coordinator when present.
 *
 * Split out of cascade-controller.js (350-line rule): the methods below moved
 * VERBATIM and are installed on HVPCascadeController.prototype by
 * cascade-controller.js, so `this` is the same instance and behavior is identical.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

export const CascadeWcOverrideMethods = {
    /**
     * Disable WooCommerce handlers
     *
     * Comprehensive strategy to prevent WooCommerce from interfering with HVP:
     * 1. Override WC form methods (update_variation_values, findMatchingVariations, onFindVariation)
     * 2. Block WC events (check_variations, woocommerce_update_variation_values)
     * 3. Set global coordination flags
     * 4. Multiple retry attempts to ensure overrides stick
     */
    disableWooCommerceHandlers() {
        // Set up global coordination flags
        this.setupCoordinationFlags();

        // Initial override attempt
        let overrideSuccess = this.overrideWooCommerceMethods();

        // Block WooCommerce variation events
        const blockEvent = (e) => {
            e.stopImmediatePropagation();
            this.log('[WC-BLOCKED] Event:', e.type);
            return false;
        };

        // Use capture phase (true) to intercept before WC handlers
        this.form.addEventListener('check_variations', blockEvent, true);
        this.form.addEventListener('woocommerce_variation_select_change', blockEvent, true);
        this.form.addEventListener('woocommerce_update_variation_values', blockEvent, true);
        this.form.addEventListener('reset_data', blockEvent, true);

        // Also block via jQuery if available (WC uses jQuery events)
        if (typeof jQuery !== 'undefined') {
            const $form = jQuery(this.form);

            // Remove existing handlers and add our blockers
            $form.off('woocommerce_update_variation_values.hvp');
            $form.on('woocommerce_update_variation_values.hvp', (e) => {
                this.log('[WC-BLOCKED] woocommerce_update_variation_values event');
                e.stopImmediatePropagation();
                return false;
            });

            $form.off('check_variations.hvp');
            $form.on('check_variations.hvp', (e) => {
                this.log('[WC-BLOCKED] check_variations event');
                e.stopImmediatePropagation();
                return false;
            });

            // Block reset_data to prevent WooCommerce from clearing dropdown values
            $form.off('reset_data.hvp');
            $form.on('reset_data.hvp', (e) => {
                // Allow reset if user clicked the reset button
                const resetLink = this.form.querySelector('.reset_variations');
                const isResetClick = resetLink && e.target === resetLink;

                if (!isResetClick) {
                    this.log('[WC-BLOCKED] reset_data event (not from user reset)');
                    e.stopImmediatePropagation();
                    return false;
                }
                // Let user-initiated reset through
                this.log('[WC-ALLOWED] reset_data event (user clicked reset)');
                return true;
            });
        }

        // Multiple retry attempts to ensure override sticks
        // WooCommerce may initialize later than us
        const retryDelays = [100, 250, 500, 1000];
        retryDelays.forEach(delay => {
            setTimeout(() => {
                if (!overrideSuccess || !this.wcOverrideApplied) {
                    overrideSuccess = this.overrideWooCommerceMethods();
                    if (overrideSuccess) {
                        this.log(`[WC-OVERRIDE] Successfully overrode WooCommerce at ${delay}ms`);
                    }
                }
            }, delay);
        });

        this.log('WooCommerce handlers disabled');
    },

    /**
     * Set up global coordination flags
     * These flags implement a mutex-like pattern for DOM updates and event handling
     * Allows coordination with other plugins (HUV, HSC, Progressive Variations)
     */
    setupCoordinationFlags() {
        // Initialize global coordination flags if not already set
        if (typeof window.__hvp_dom_updating === 'undefined') {
            window.__hvp_dom_updating = false;
        }
        if (typeof window.__hvp_event_handled === 'undefined') {
            window.__hvp_event_handled = false;
        }

        // Expose coordination functions globally
        window.setHVPDOMUpdating = (updating) => {
            window.__hvp_dom_updating = updating;
            if (updating) {
                // Auto-reset after 100ms to prevent deadlocks
                setTimeout(() => {
                    window.__hvp_dom_updating = false;
                }, 100);
            }
        };

        window.setHVPEventHandled = (handled) => {
            window.__hvp_event_handled = handled;
            if (handled) {
                // Auto-reset after 50ms for next event cycle
                setTimeout(() => {
                    window.__hvp_event_handled = false;
                }, 50);
            }
        };

        this.log('Coordination flags initialized');
    },

    /**
     * Override WooCommerce form methods
     *
     * @returns {boolean} Whether override was successful
     */
    overrideWooCommerceMethods() {
        // Check for jQuery and WC form data
        if (typeof jQuery === 'undefined') {
            this.log('[WC-OVERRIDE] jQuery not available');
            return false;
        }

        const $form = jQuery(this.form);
        const wcForm = $form.data('wc_variation_form');

        if (!wcForm) {
            this.log('[WC-OVERRIDE] WooCommerce form not initialized yet');
            return false;
        }

        let overridden = false;

        // Override update_variation_values - prevents option filtering
        // This is the main method WC uses to remove "incompatible" options
        if (wcForm.update_variation_values && !wcForm._hvp_override_applied) {
            wcForm._original_update_variation_values = wcForm.update_variation_values;
            wcForm.update_variation_values = () => {
                this.log('[WC-BLOCKED] Blocked WooCommerce from filtering options');
                // Don't call the original - it removes options we want to keep
                return false;
            };
            wcForm._hvp_override_applied = true;
            overridden = true;
            this.log('[WC-OVERRIDE] Overrode update_variation_values');
        }

        // Override findMatchingVariations to prevent interference
        // WC uses this to filter variations - we handle matching ourselves
        if (wcForm.findMatchingVariations && !wcForm._hvp_find_override_applied) {
            wcForm._original_findMatchingVariations = wcForm.findMatchingVariations;
            wcForm.findMatchingVariations = (variations, settings) => {
                this.log('[WC-OVERRIDE] Using HVP variation matching instead of WC');
                // Return all variations - let HVP handle filtering
                return variations;
            };
            wcForm._hvp_find_override_applied = true;
            overridden = true;
            this.log('[WC-OVERRIDE] Overrode findMatchingVariations');
        }

        // Override onFindVariation to prevent automatic updates
        // WC calls this when a variation is found - it can interfere with our UI
        if (wcForm.onFindVariation && !wcForm._hvp_onFind_override_applied) {
            const self = this;
            wcForm._original_onFindVariation = wcForm.onFindVariation;
            wcForm.onFindVariation = function(event, variation) {
                self.log('[WC-OVERRIDE] Intercepted WooCommerce variation found event');

                // Let original run but preserve dropdown values after WC processes
                if (wcForm._original_onFindVariation) {
                    // Store current values
                    const currentValues = {};
                    self.dropdowns.forEach((select, attrName) => {
                        currentValues[attrName] = select.value;
                    });

                    // Call original
                    wcForm._original_onFindVariation.call(this, event, variation);

                    // Restore values if they were changed
                    self.dropdowns.forEach((select, attrName) => {
                        if (currentValues[attrName] && select.value !== currentValues[attrName]) {
                            select.value = currentValues[attrName];
                            self.log('[WC-OVERRIDE] Restored value for', attrName);
                        }
                    });
                }
            };
            wcForm._hvp_onFind_override_applied = true;
            overridden = true;
            this.log('[WC-OVERRIDE] Overrode onFindVariation');
        }

        this.wcOverrideApplied = overridden;
        return overridden;
    },

    /**
     * Check if another plugin is updating DOM (for coordination)
     * @returns {boolean}
     */
    isOtherPluginUpdatingDOM() {
        return window.__huv_dom_updating === true ||
               window.__hsc_dom_updating === true ||
               window.__progressive_dom_updating === true;
    },

    /**
     * Set DOM updating flag with auto-reset
     * @param {boolean} updating
     */
    setDOMUpdating(updating) {
        window.__hvp_dom_updating = updating;
        if (updating) {
            setTimeout(() => {
                window.__hvp_dom_updating = false;
            }, 100);
        }
    },

    /**
     * Register with HUV Coordinator
     */
    registerWithHUV() {
        if (typeof window.HUVCoordinator !== 'undefined') {
            try {
                window.HUVCoordinator.registerPlugin('haru-variations-pro', {
                    capabilities: ['cascade', 'server-side'],
                    version: '1.0.0',

                    onSelectionChanged: (data) => {
                        this.log('HUV selection changed:', data);
                    },

                    onVariationMatched: (variation) => {
                        this.log('HUV variation matched:', variation);
                    }
                });

                this.log('Registered with HUV Coordinator');

            } catch (error) {
                window.hvpData && window.hvpData.debug && console.warn('[HVP] Failed to register with HUV:', error);
            }
        }
    },
};

export default CascadeWcOverrideMethods;
